import React, { useEffect, useMemo, useState } from "react";
import { ArrowUpRight, UsersRound } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { Reveal, Kicker } from "../motion";
import workingCapitalImg from "../../assets/goals/01-working-capital.webp";
import equipmentImg from "../../assets/goals/02-equipment-purchase.webp";
import sowingImg from "../../assets/goals/03-sowing-campaign.webp";
import livestockImg from "../../assets/goals/04-livestock.webp";
import productionImg from "../../assets/goals/05-production-development.webp";
import premisesImg from "../../assets/goals/06-premises-modernization.webp";
import otherBusinessImg from "../../assets/goals/07-other-business-purposes.webp";

const GOAL_IMAGES = [
  workingCapitalImg,
  equipmentImg,
  sowingImg,
  livestockImg,
  productionImg,
  premisesImg,
  otherBusinessImg,
];

const formatNumber = (value) => Math.round(value).toLocaleString("ru-RU");
const formatTenge = (value) => `${formatNumber(value)} ₸`;

const AMOUNT_MIN = 500000;
const AMOUNT_MAX = 20000000;
const TERM_MIN = 12;
const TERM_MAX = 36;

export function Products({ hideIntro = false }) {
  const { c, openApply } = useApp();
  const p = c.products;
  const [amount, setAmount] = useState(500000);
  const [term, setTerm] = useState(12);
  const calculation = useMemo(() => {
    const monthlyRate = 0.38 / 12;
    const payment = amount * (monthlyRate * (1 + monthlyRate) ** term) / ((1 + monthlyRate) ** term - 1);
    const total = payment * term;
    return { payment, total, overpayment: total - amount };
  }, [amount, term]);

  return (
    <section id="products" data-testid="products-section" className="relative bg-[#f5f7fa] py-24 md:py-32">
      <div className="mx-auto max-w-[1240px] px-5 md:px-8">
        {!hideIntro && <Reveal className="max-w-2xl">
          <Kicker>{p.kicker}</Kicker>
          <h2 className="mt-5 font-display text-4xl font-bold tracking-tight text-[#0a1a2e] sm:text-5xl lg:text-6xl">
            {p.title}
          </h2>
          <p className="mt-4 text-base text-[#3e516a] md:text-lg">{p.subtitle}</p>
        </Reveal>}

        <div className="mt-12">
          <h2 className="font-display text-3xl font-bold tracking-tight text-[#0a1a2e] sm:text-4xl">
            {p.businessTitle}
          </h2>
        </div>

        <div className="mt-7 grid grid-cols-1 gap-6 md:grid-cols-3">
          {p.goals.map((item, i) => (
            <Reveal
              key={item.name}
              delay={i * 0.08}
              className="card-lift group flex flex-col overflow-hidden rounded-3xl border border-black/5 bg-white"
            >
              <img src={GOAL_IMAGES[i]} alt="" loading="lazy" decoding="async" className="aspect-[4/3] w-full object-cover" />
              <div className="flex flex-1 flex-col p-7 md:p-8">
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#002355]/60">0{i + 1}</span>
                <h3 className="mt-4 font-display text-2xl font-bold tracking-tight text-[#0a1a2e]">{item.name}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#3e516a]">{item.desc}</p>
                <button
                  data-testid={`goal-apply-${i}`}
                  onClick={openApply}
                  className="group/btn mt-auto inline-flex items-center justify-between gap-2 pt-7 text-sm font-bold text-[#002355]"
                >
                  {c.cta.apply}
                  <ArrowUpRight size={18} className="transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                </button>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-14 overflow-hidden rounded-3xl bg-[#002355] text-[#f5f7fa]">
          <div className="grid gap-8 p-8 md:grid-cols-[auto_1fr_auto] md:items-center md:p-10">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#e8a93d] text-[#002355]">
              <UsersRound size={30} strokeWidth={1.7} />
            </div>
            <div>
              <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{p.individuals.title}</h2>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[#f5f7fa]/75 md:text-base">{p.individuals.desc}</p>
              <div className="mt-5 flex flex-wrap gap-x-7 gap-y-3">
                {p.individuals.rows.map(([label, value]) => (
                  <div key={label}>
                    <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#f5f7fa]/45">{label}</p>
                    <p className="mt-1 text-sm font-semibold text-[#f5f7fa]">{value}</p>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#e8a93d]">{p.individuals.note}</p>
            </div>
            <button
              data-testid="individuals-apply-btn"
              onClick={openApply}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#e8a93d] px-6 py-3.5 text-sm font-bold text-[#002355] transition-transform duration-300 hover:scale-105"
            >
              {c.cta.apply}
              <ArrowUpRight size={17} />
            </button>
          </div>
        </Reveal>

        <Reveal id="calculator" className="mt-14 scroll-mt-24 overflow-hidden rounded-3xl bg-[#002355] text-[#f5f7fa]">
          <div className="grid gap-10 p-7 md:grid-cols-[1fr_0.9fr] md:p-10">
            <div>
              <Kicker className="text-[#e8a93d]">{p.calculator.kicker}</Kicker>
              <h2 className="mt-4 max-w-lg font-display text-3xl font-bold tracking-tight sm:text-4xl">{p.calculator.title}</h2>
              <div className="mt-9 space-y-8">
                <RangeField
                  label={p.calculator.amount}
                  testid="calc-amount"
                  value={amount}
                  min={AMOUNT_MIN}
                  max={AMOUNT_MAX}
                  step={100000}
                  unit="₸"
                  formatBound={formatTenge}
                  onChange={setAmount}
                />
                <RangeField
                  label={p.calculator.term}
                  testid="calc-term"
                  value={term}
                  min={TERM_MIN}
                  max={TERM_MAX}
                  step={1}
                  unit={p.calculator.termUnit}
                  formatBound={(value) => `${value} ${p.calculator.termUnit}`}
                  onChange={setTerm}
                />
              </div>
            </div>
            <div className="flex flex-col justify-center rounded-3xl bg-[#f5f7fa] p-7 text-[#0a1a2e] md:p-8">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#3e516a]/70">{p.calculator.result}</p>
              <div className="mt-5 space-y-5">
                <ResultRow label={p.calculator.monthly} value={formatTenge(calculation.payment)} prominent />
                <ResultRow label={p.calculator.overpayment} value={formatTenge(calculation.overpayment)} />
              </div>
              <p className="mt-7 text-xs leading-relaxed text-[#3e516a]">{p.calculator.note}</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function RangeField({ label, testid, value, min, max, step, unit, formatBound, onChange }) {
  // The text field keeps its own draft so partial input (an empty box, a
  // half-typed number) is not clamped out from under the person typing.
  const [draft, setDraft] = useState(() => formatNumber(value));

  useEffect(() => {
    setDraft(formatNumber(value));
  }, [value]);

  const commitDraft = () => {
    // Strip the digit grouping we render, so "3 000 000" parses back.
    const parsed = Number(draft.replace(/\s/g, ""));
    if (!draft.trim() || !Number.isFinite(parsed)) {
      setDraft(formatNumber(value));
      return;
    }

    const clamped = Math.min(max, Math.max(min, Math.round(parsed)));
    // Re-render the draft even when the clamped value equals the current one,
    // otherwise an out-of-range entry would stay in the box.
    setDraft(formatNumber(clamped));
    onChange(clamped);
  };

  return (
    <div className="block">
      <div className="flex items-center justify-between gap-4">
        <label htmlFor={testid} className="text-sm font-semibold text-[#f5f7fa]/80">
          {label}
        </label>
        <span className="flex items-baseline gap-1.5 rounded-xl border border-[#f5f7fa]/15 bg-[#f5f7fa]/5 px-3 py-1.5 focus-within:border-[#e8a93d]">
          <input
            id={testid}
            data-testid={testid}
            type="text"
            inputMode="numeric"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={commitDraft}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                commitDraft();
              }
            }}
            className="w-28 bg-transparent text-right font-display text-xl font-bold text-[#e8a93d] outline-none"
          />
          <span className="text-sm font-semibold text-[#e8a93d]/70">{unit}</span>
        </span>
      </div>
      <input
        type="range"
        aria-label={label}
        data-testid={`${testid}-range`}
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        className="mt-4 h-2 w-full cursor-pointer accent-[#e8a93d]"
      />
      <span className="mt-2 flex justify-between text-xs text-[#f5f7fa]/45">
        <span>{formatBound(min)}</span>
        <span>{formatBound(max)}</span>
      </span>
    </div>
  );
}

function ResultRow({ label, value, prominent = false }) {
  return (
    <div className="flex items-end justify-between gap-4 border-b border-[#002355]/10 pb-3 last:border-b-0 last:pb-0">
      <span className="text-sm text-[#3e516a]">{label}</span>
      <span className={`text-right font-bold ${prominent ? "font-display text-2xl text-[#002355]" : "text-base text-[#0a1a2e]"}`}>
        {value}
      </span>
    </div>
  );
}
